'use client';

import { Suspense, startTransition, useEffect, useRef, useState } from 'react';

interface DeferredSectionProps {
    id: string;
    label: string;
    children: React.ReactNode;
    rootMargin?: string;
    prepareOnMount?: boolean;
    placeholderClassName?: string;
}

function SectionPlaceholder({ label, className = '' }: { label: string; className?: string }) {
    return (
        <div
            className={`flex min-h-screen items-center justify-center bg-white dark:bg-[#0D0016] ${className}`}
            role="status"
            aria-label={`Loading ${label}`}
        >
            <div className="h-10 w-10 animate-pulse rounded-full border-2 border-white/10 bg-gradient-to-br from-dark-blue/30 to-dark-purple/30" />
        </div>
    );
}

export default function DeferredSection({
    id,
    label,
    children,
    rootMargin = '0px 0px -25% 0px',
    prepareOnMount = false,
    placeholderClassName = '',
}: DeferredSectionProps) {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [shouldLoad, setShouldLoad] = useState(false);

    useEffect(() => {
        if (!prepareOnMount || shouldLoad) return;

        const timer = window.setTimeout(() => {
            startTransition(() => setShouldLoad(true));
        }, 0);

        return () => window.clearTimeout(timer);
    }, [prepareOnMount, shouldLoad]);

    useEffect(() => {
        const section = sectionRef.current;
        if (!section || shouldLoad || prepareOnMount) return;

        if (!('IntersectionObserver' in window)) {
            setShouldLoad(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                // Treat below-the-fold rendering as non-urgent so React can yield
                // immediately to wheel and touch input on slower devices.
                startTransition(() => setShouldLoad(true));
                observer.disconnect();
            },
            { rootMargin },
        );
        observer.observe(section);

        return () => observer.disconnect();
    }, [prepareOnMount, rootMargin, shouldLoad]);

    return (
        <div id={id} ref={sectionRef} className="relative scroll-mt-24" data-section-reveal>
            {shouldLoad ? (
                <Suspense fallback={<SectionPlaceholder label={label} className={placeholderClassName} />}>
                    {children}
                </Suspense>
            ) : (
                <SectionPlaceholder label={label} className={placeholderClassName} />
            )}
        </div>
    );
}
